import React, { useEffect, useMemo, useState } from 'react';
import {
  X as LucideX,
  Camera as LucideCamera,
  MapPin as LucideMapPin,
  Search as LucideSearch
} from 'lucide-react';
import type {
  PlayerRouteMap,
  PlayerRouteMapHotspot,
  PlayerRouteMapSpot,
  RouteMapHotspotActions
} from '../utils/routeMapAdapter';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;
const X = LucideX as unknown as IconComponent;
const Camera = LucideCamera as unknown as IconComponent;
const MapPin = LucideMapPin as unknown as IconComponent;
const Search = LucideSearch as unknown as IconComponent;

export type RouteMapData = PlayerRouteMap;

export function RouteMapButton({
  isOpen,
  onClick,
  position = 'top-right',
}: {
  isOpen: boolean;
  onClick: () => void;
  position?: 'top-left' | 'top-right';
}): JSX.Element {
  return (
    <button
      type="button"
      className={`route-map-btn route-map-btn--${position}${isOpen ? ' is-open' : ''}`}
      onClick={onClick}
      aria-label="現在地マップを表示"
      aria-pressed={isOpen}
    >
      <span className="route-map-btn__label">現在地</span>
      <span className="route-map-btn__icon" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    </button>
  );
}

export function RouteMapWindow({
  routeMap,
  isOpen,
  onClose,
  onDiscoverHotspot,
}: {
  routeMap?: RouteMapData;
  isOpen: boolean;
  onClose: () => void;
  onDiscoverHotspot?: (spotId: string, hotspotId: string, actions?: RouteMapHotspotActions) => void;
}): JSX.Element | null {
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);
  const [photoOpen, setPhotoOpen] = useState(false);

  useEffect(() => {
    if (!routeMap) {
      setSelectedSpotId(null);
      setPhotoOpen(false);
      return;
    }
    const target = routeMap.spots[routeMap.targetIndex] ?? routeMap.spots[routeMap.currentIndex] ?? routeMap.spots[0];
    setSelectedSpotId((current) => {
      if (current && routeMap.spots.some((spot) => spot.id === current)) {
        return current;
      }
      return target?.id ?? null;
    });
  }, [routeMap]);

  useEffect(() => {
    setPhotoOpen(false);
  }, [selectedSpotId]);

  const selectedSpot = useMemo(() => {
    if (!routeMap) return null;
    return routeMap.spots.find((spot) => spot.id === selectedSpotId) ?? routeMap.spots[routeMap.targetIndex] ?? routeMap.spots[0] ?? null;
  }, [routeMap, selectedSpotId]);

  if (!isOpen || !routeMap) {
    return null;
  }

  return (
    <div className="route-map-window" role="dialog" aria-modal="true" aria-label="現在地マップ">
      <div className="route-map-window__panel">
        <div className="route-map-window__header">
          <div>
            <p>現在地マップ</p>
            <h2>{routeMap.title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="マップを閉じる">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="route-map-window__content">
          <RouteMapCanvas
            routeMap={routeMap}
            selectedSpotId={selectedSpot?.id}
            onSelectSpot={setSelectedSpotId}
          />

          {selectedSpot && !photoOpen && (
            <RouteMapSpotPanel spot={selectedSpot} onOpenPhoto={() => setPhotoOpen(true)} />
          )}

          {selectedSpot && photoOpen && (
            <RouteMapPhotoPanel
              spot={selectedSpot}
              onBack={() => setPhotoOpen(false)}
              onDiscoverHotspot={onDiscoverHotspot}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function RouteMapCanvas({
  routeMap,
  selectedSpotId,
  onSelectSpot,
}: {
  routeMap: RouteMapData;
  selectedSpotId?: string;
  onSelectSpot: (spotId: string) => void;
}): JSX.Element {
  const spotById = useMemo(() => new Map(routeMap.spots.map((spot) => [spot.id, spot])), [routeMap.spots]);

  // 現在地→次目的地の区間を強調するためのspot idペア。
  // 同一spot（目的地未設定）の場合は強調しない。
  const currentSpotId = routeMap.spots[routeMap.currentIndex]?.id;
  const targetSpotId = routeMap.spots[routeMap.targetIndex]?.id;
  const activeSegment =
    currentSpotId && targetSpotId && currentSpotId !== targetSpotId
      ? new Set([`${currentSpotId}->${targetSpotId}`, `${targetSpotId}->${currentSpotId}`])
      : null;

  return (
    <div className={`route-map-canvas${routeMap.mapImageUrl ? ' has-image' : ' is-schematic'}`}>
      {routeMap.mapImageUrl ? (
        <img className="route-map-canvas__image" src={routeMap.mapImageUrl} alt={routeMap.mapImageAlt ?? routeMap.title} />
      ) : (
        <div className="route-map-canvas__schematic" aria-hidden="true" />
      )}

      <svg className="route-map-canvas__paths" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {routeMap.paths
          .filter((path) => path.isVisible)
          .map((path) => {
            const from = spotById.get(path.fromSpotId);
            const to = spotById.get(path.toSpotId);
            if (!from || !to) return null;
            const isActive = activeSegment?.has(`${path.fromSpotId}->${path.toSpotId}`) ?? false;
            return (
              <line
                key={path.id}
                className={isActive ? 'is-route-active' : undefined}
                x1={from.xPct}
                y1={from.yPct}
                x2={to.xPct}
                y2={to.yPct}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
      </svg>

      <div className="route-map-canvas__spots">
        {routeMap.spots.map((spot, index) => (
          <button
            key={spot.id}
            type="button"
            className={[
              'route-map-marker',
              spot.isCurrent ? 'is-current' : '',
              spot.isTarget ? 'is-target' : '',
              spot.isCompleted ? 'is-completed' : '',
              spot.isLocked ? 'is-locked' : '',
              selectedSpotId === spot.id ? 'is-selected' : '',
            ].filter(Boolean).join(' ')}
            style={{ left: `${spot.xPct}%`, top: `${spot.yPct}%` }}
            onClick={() => onSelectSpot(spot.id)}
            aria-label={`${index + 1}. ${spot.label}`}
            title={spot.label}
          >
            <span className="route-map-marker__dot">{spot.marker?.icon ?? index + 1}</span>
            <span className="route-map-marker__label">{spot.shortLabel ?? spot.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function RouteMapSpotPanel({
  spot,
  onOpenPhoto,
}: {
  spot: PlayerRouteMapSpot;
  onOpenPhoto: () => void;
}): JSX.Element {
  const hasPhoto = Boolean(spot.photo);
  return (
    <div className="route-map-spot-panel">
      <div className="route-map-spot-panel__title">
        <MapPin className="w-4 h-4" />
        <div>
          <span>{spot.isCurrent ? '現在地' : spot.isTarget ? '目的地' : spot.isCompleted ? '確認済み' : '地点'}</span>
          <strong>{spot.label}</strong>
        </div>
      </div>

      {spot.description && <p className="route-map-spot-panel__description">{spot.description}</p>}

      <div className="route-map-spot-panel__chips">
        {spot.keyword && <span>{spot.keyword}</span>}
        {spot.ability && <span>{spot.ability}</span>}
        {spot.relatedClueIds.length > 0 && <span>手がかり {spot.relatedClueIds.length}</span>}
      </div>

      {spot.isLocked && (
        <p className="route-map-spot-panel__locked">この地点はまだ未開放です。先の行動で詳細が見えるようになります。</p>
      )}

      <button
        type="button"
        className="route-map-photo-btn"
        onClick={onOpenPhoto}
        disabled={!hasPhoto}
      >
        <Camera className="w-4 h-4" />
        {hasPhoto ? '現地写真を見る' : '現地写真は未設定'}
      </button>
    </div>
  );
}

function RouteMapPhotoPanel({
  spot,
  onBack,
  onDiscoverHotspot,
}: {
  spot: PlayerRouteMapSpot;
  onBack: () => void;
  onDiscoverHotspot?: (spotId: string, hotspotId: string, actions?: RouteMapHotspotActions) => void;
}): JSX.Element {
  const photo = spot.photo;
  const hotspots = photo?.hotspots ?? [];

  return (
    <div className="route-map-photo-panel">
      <div className="route-map-photo-panel__header">
        <button type="button" onClick={onBack}>
          地点へ戻る
        </button>
        <strong>{spot.label}</strong>
      </div>

      <div className={`route-map-photo-stage${photo?.resolvedUrl ? ' has-photo' : ' is-empty'}`}>
        {photo?.resolvedUrl ? (
          <img src={photo.resolvedUrl} alt={photo.caption ?? `${spot.label}の現地写真`} />
        ) : (
          <div className="route-map-photo-stage__placeholder">
            <Camera className="w-8 h-8" />
            <span>現地写真は未設定</span>
          </div>
        )}

        {hotspots.map((hotspot) => (
          <HotspotButton
            key={hotspot.id}
            hotspot={hotspot}
            onClick={() => onDiscoverHotspot?.(spot.id, hotspot.id, hotspot.actions)}
          />
        ))}
      </div>

      {photo?.caption && <p className="route-map-photo-panel__caption">{photo.caption}</p>}

      <div className="route-map-photo-panel__hotspots">
        <p>
          <Search className="w-3.5 h-3.5" />
          写真内の反応
        </p>
        {hotspots.length === 0 ? (
          <span>この写真に探索ポイントはまだ設定されていません。</span>
        ) : (
          hotspots.map((hotspot) => (
            <button
              key={hotspot.id}
              type="button"
              className={hotspot.isDiscovered ? 'is-discovered' : ''}
              onClick={() => onDiscoverHotspot?.(spot.id, hotspot.id, hotspot.actions)}
            >
              <strong>{hotspot.label}</strong>
              <span>{hotspot.isDiscovered ? hotspot.revealText ?? '確認済み' : 'タップして確認'}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function HotspotButton({
  hotspot,
  onClick,
}: {
  hotspot: PlayerRouteMapHotspot;
  onClick: () => void;
}): JSX.Element {
  const style = buildHotspotStyle(hotspot);
  return (
    <button
      type="button"
      className={`route-map-hotspot route-map-hotspot--${hotspot.shape}${hotspot.isDiscovered ? ' is-discovered' : ''}`}
      style={style}
      onClick={onClick}
      aria-label={hotspot.label}
      title={hotspot.label}
    />
  );
}

function buildHotspotStyle(hotspot: PlayerRouteMapHotspot): React.CSSProperties {
  if (hotspot.shape === 'rect') {
    return {
      left: `${hotspot.xPct ?? 50}%`,
      top: `${hotspot.yPct ?? 50}%`,
      width: `${hotspot.widthPct ?? 12}%`,
      height: `${hotspot.heightPct ?? 12}%`,
    };
  }
  if (hotspot.shape === 'circle') {
    const radius = hotspot.radiusPct ?? 7;
    return {
      left: `${hotspot.xPct ?? 50}%`,
      top: `${hotspot.yPct ?? 50}%`,
      width: `${radius * 2}%`,
      height: `${radius * 2}%`,
      transform: 'translate(-50%, -50%)',
    };
  }
  if (hotspot.shape === 'polygon' && hotspot.points?.length) {
    const xValues = hotspot.points.map((point) => point.xPct);
    const yValues = hotspot.points.map((point) => point.yPct);
    const minX = Math.min(...xValues);
    const minY = Math.min(...yValues);
    return {
      left: `${minX}%`,
      top: `${minY}%`,
      width: `${Math.max(...xValues) - minX}%`,
      height: `${Math.max(...yValues) - minY}%`,
    };
  }
  return {
    left: `${hotspot.xPct ?? 50}%`,
    top: `${hotspot.yPct ?? 50}%`,
    transform: 'translate(-50%, -50%)',
  };
}
